import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const SignUp: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithGoogle, signInWithPhone, confirmPhoneSignIn } = useAuth() as any;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError('Échec de l\'inscription avec Google');
      setLoading(false);
    }
  };

  const handlePhoneSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!phoneNumber) {
      setError('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    setLoading(true);
    try {
      await signInWithPhone(phoneNumber);
      setShowVerificationDialog(true);
    } catch (err) {
      setError('Échec de l\'envoi du code de vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await confirmPhoneSignIn(phoneNumber, verificationCode);
      setShowVerificationDialog(false);
      navigate('/');
    } catch (err) {
      setError('Code de vérification invalide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            overflow: 'hidden',
            borderRadius: 2,
            minHeight: isMobile ? 'auto' : 600,
          }}
        >
          {/* Left side - Form */}
          <Box
            sx={{
              flex: 1,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ maxWidth: 400, mx: 'auto', width: '100%' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  textAlign: 'center',
                  mb: 4,
                }}
              >
                Créer un compte
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignUp}
                disabled={loading}
                sx={{
                  mb: 3,
                  py: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#dadce0',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: 1,
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#4285f4',
                  },
                }}
              >
                Inscription avec Google
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Ou inscription par téléphone
                </Typography>
              </Divider>

              <form onSubmit={handlePhoneSignUp}>
                <Box 
                  sx={{ 
                    mb: 2,
                    '& .PhoneInput': {
                      backgroundColor: 'background.paper',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      transition: 'all 0.3s ease-in-out',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-within': {
                        borderColor: '#1e40af',
                        boxShadow: '0 0 0 3px rgba(30, 64, 175, 0.1)',
                        transform: 'translateY(-1px)',
                      },
                    },
                    '& .PhoneInputInput': {
                      height: '56px',
                      padding: '0 16px',
                      border: 'none',
                      fontSize: '1rem',
                      fontWeight: '500',
                      color: 'text.primary',
                      backgroundColor: 'transparent',
                      '&:focus': {
                        outline: 'none',
                        backgroundColor: 'transparent',
                      },
                      '&::placeholder': {
                        color: 'text.secondary',
                        fontWeight: '400',
                      },
                    },
                    '& .PhoneInputCountry': {
                      marginRight: '12px',
                      padding: '0 16px',
                      borderRight: '1px solid #e5e7eb',
                      backgroundColor: 'background.default',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    },
                    '& .PhoneInputCountrySelect': {
                      backgroundColor: 'transparent',
                      border: 'none',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: 'text.primary',
                      cursor: 'pointer',
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                    '& .PhoneInputCountryIcon': {
                      width: '20px',
                      height: '15px',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <PhoneInput
                    placeholder="Entrez votre numéro de téléphone"
                    value={phoneNumber}
                    onChange={setPhoneNumber as any}
                    defaultCountry="SN"
                    international
                    countryCallingCodeEditable={false}
                  />
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PhoneIcon />}
                  type="submit"
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    borderRadius: '12px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                      boxShadow: '0 6px 16px rgba(30, 64, 175, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: '#9ca3af',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                  }}
                >
                  {loading ? 'Envoi en cours...' : 'Recevoir le code'}
                </Button>
              </form>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Ou
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="text"
                component={RouterLink}
                to="/signin"
                sx={{ py: 1.5 }}
              >
                Déjà un compte ? Se connecter
              </Button>
            </Box>
          </Box>

          {/* Right side - Illustration (optional) */}
          <Box
            sx={{
              flex: 1,
              display: isMobile ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.light',
            }}
          >
            {/* You can add an illustration or logo here */}
          </Box>
        </Paper>

        {/* Phone verification dialog */}
        <Dialog open={showVerificationDialog} onClose={() => setShowVerificationDialog(false)}>
          <DialogTitle>Code de vérification</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Code reçu par SMS"
              type="text"
              fullWidth
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowVerificationDialog(false)}>Annuler</Button>
            <Button onClick={handleVerificationSubmit} variant="contained" disabled={loading}>
              Valider
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SignUp; 